import { inject, computed } from "@angular/core";
import { signalStore, withComputed, withMethods, patchState, withHooks } from "@ngrx/signals";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { tap, pipe, filter, switchMap, Observable, iif } from "rxjs";
import {
    withRequestStatus,
    setPending,
    setFulfilled,
    setError,
} from "../extensions/request-status.feature";
import { withRouteParams } from "../extensions/route-params.feature";
import { UsersStore } from "./users.store";
import { ApiUsersService } from "../../services/api-users.service";
import { setEntity } from "@ngrx/signals/entities";
import { HttpErrorResponse } from "@angular/common/http";
import { UserBackofficeViewModel } from "@playcharge/common/models/backend/users/backoffice/user-backoffice-view-model";

export type UserStore = InstanceType<typeof UserStore>;

export const UserStore = signalStore(
    { protectedState: false },
    withRouteParams({ email: (param) => param }),
    withRequestStatus(),
    withComputed(({ email }, usersStore = inject(UsersStore)) => ({
        user: computed(() => (email() ? usersStore.entityMap()[email()!] : null)),
    })),
    withMethods(
        (store, usersStore = inject(UsersStore), apiUsersService = inject(ApiUsersService)) => {
            const loadUser = rxMethod<{
                email: string;
                next?: (user: UserBackofficeViewModel) => void;
                error?: (error: HttpErrorResponse) => void;
            }>(
                pipe(
                    filter((request) => !!request.email),
                    tap(() => patchState(store, setPending())),
                    switchMap((request) => {
                        return apiUsersService.getByEmail(request.email).pipe(
                            tap({
                                next: (user) => {
                                    patchState(store, setFulfilled());
                                    patchState(
                                        usersStore,
                                        setEntity(user, { selectId: (user) => user.email }),
                                    );
                                    request.next?.(user);
                                },
                                error: (error) => patchState(store, setError(error.error.error)),
                            }),
                        );
                    }),
                ),
            );

            const forceApproveConversion = rxMethod<{
                conversionId: number;
                reason: string;
                next?: () => void;
                error?: (error: HttpErrorResponse) => void;
            }>(
                pipe(
                    switchMap((request) => {
                        return apiUsersService
                            .forceApproveConversion(request.conversionId, request.reason)
                            .pipe(
                                tap({
                                    next: () => {
                                        loadUser({ email: store.email()! });
                                        request.next?.();
                                    },
                                    error: (error: HttpErrorResponse) => request.error?.(error),
                                }),
                            );
                    }),
                ),
            );

            const reapproveConversion = rxMethod<{
                conversionId: number;
                next?: () => void;
                error?: (error: HttpErrorResponse) => void;
            }>(
                pipe(
                    switchMap((request) => {
                        return apiUsersService.reapproveConversion(request.conversionId).pipe(
                            tap({
                                next: () => {
                                    loadUser({ email: store.email()! });
                                    request.next?.();
                                },
                                error: (error: HttpErrorResponse) => request.error?.(error),
                            }),
                        );
                    }),
                ),
            );

            const balanceCorrection = rxMethod<{
                amount: number;
                expAmount: number;
                reason: string;
                next?: () => void;
                error?: (error: HttpErrorResponse) => void;
            }>(
                pipe(
                    switchMap((request) => {
                        return apiUsersService.balanceCorrection(store.email()!, request.amount, request.expAmount, request.reason).pipe(
                            tap({
                                next: () => {
                                    loadUser({ email: store.email()! });
                                    request.next?.();
                                },
                                error: (error: HttpErrorResponse) => request.error?.(error),
                            }),
                        );
                    }),
                ),
            );

            const banUser = rxMethod<{
                userId: number;
                reason: string;
                next?: (user: UserBackofficeViewModel) => void;
                error?: (error: HttpErrorResponse) => void;
            }>(
                pipe(
                    switchMap((request) => {
                        return apiUsersService.banUser(request.userId, request.reason).pipe(
                            tap({
                                next: () => {
                                    loadUser({ email: store.user()!.profileId, next: request.next, error: request.error });
                                },
                                error: (error: HttpErrorResponse) => request.error?.(error),
                            }),
                        );
                    }),
                ),
            );

            const unbanUser = rxMethod<{
                userId: number;
                reason: string;
                next?: (user: UserBackofficeViewModel) => void;
                error?: (error: HttpErrorResponse) => void;
            }>(
                pipe(
                    switchMap((request) => {
                        return apiUsersService.unbanUser(request.userId, request.reason).pipe(
                            tap({
                                next: () => {
                                    loadUser({ email: store.user()!.profileId, next: request.next, error: request.error });
                                },
                                error: (error: HttpErrorResponse) => request.error?.(error),
                            }),
                        );
                    }),
                ),
            );

            return {
                loadUser,
                forceApproveConversion,
                reapproveConversion,
                balanceCorrection,
                banUser,
                unbanUser,
            };
        },
    ),
    
    withHooks({
        onInit({ loadUser, email }) {
            loadUser({ email: email()! });
        },
    }),
);
